import moment from "moment/moment";
import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { z } from "zod";
import { $axios } from "../Helper/axios.config";
import { userAccountSchema } from "../Helper/validators";
import Sidebar from "../components/Sidebar";

export default function UserAccount() {
	const [errors, setErrors] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState({
		contactAddress: "",
		contactEmail: "",
		contactPhone: "",
		organizationAddress: "",
		organizationEmail: "",
		organizationName: "",
		organizationPosition: "",
		organizationTelephone: "",
		hide: false,
	});

	const handleChange = (e) => {
		const { name, value } = e.target;
		setData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	const userId = localStorage.getItem("userid");

	const handleSubmit = async (event) => {
		event.preventDefault();
		setIsLoading(true);

		try {
			userAccountSchema.parse(data);
			await $axios.put(`/v2/users/user/${userId}`, {
				...data,
				contactInformation: {
					contactAddress: data.contactAddress,
					contactEmail: data.contactEmail,
					contactPhone: data.contactPhone,
				},
				workInformation: {
					organizationAddress: data.organizationAddress,
					organizationEmail: data.organizationEmail,
					organizationName: data.organizationName,
					organizationPosition: data.organizationPosition,
					organizationTelephone: data.organizationTelephone,
				},
			});

			await Swal.fire({
				title: "Success",
				text: "User information updated successfully",
				icon: "success",
			});
		} catch (error) {
			if (error instanceof z.ZodError) {
				const formattedErrors = error.errors.reduce((acc, curr) => {
					acc[curr.path[0]] = curr.message;
					return acc;
				}, {});
				setErrors(formattedErrors);
				await Swal.fire({
					title: "Error",
					text: "Please fill in all required fields correctly",
					icon: "error",
				});
			} else {
				console.log(error);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const getUserInfo = async () => {
		try {
			const res = await $axios.get(
				`/Registration/getRegistrationByUserID/${userId}`,
			);
			const userInfo = res.data[0];

			setData({
				contactAddress: userInfo.contactAddress || userInfo.contactaddress,
				contactEmail: userInfo.contactEmail || userInfo.contactemail,
				contactPhone: userInfo.contactTelephone || userInfo.contacttelephone,
				organizationAddress: userInfo.organizationAddress || userInfo.organizationaddress,
				organizationEmail: userInfo.organizationEmail || userInfo.organizationemail,
				organizationName: userInfo.organization,
				organizationPosition: userInfo.organizationPosition || userInfo.organizationposition,
				organizationTelephone: userInfo.organizationTelephone || userInfo.organizationtelephone,
				hide: false,
			});
		} catch (error) {
			console.log(error);
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		getUserInfo();
	}, []);

	return (
		<div className="g-sidenav-show">
			<Sidebar />

			<main
				className="main-content position-relative max-height-vh-100 h-100 border-radius-lg "
				style={{ width: "80%", float: "right" }}
			>
				<div
					className="container-fluid justify-content-center py-4"
					style={{ width: "100%", position: "relative", left: 0 }}
				>
					<div className="container-fluid px-4">
						<div className="rown">
							<div
								className="col-12"
								style={{
									backgroundColor: "white",
									padding: 20,
								}}
							>
								<div>
									{/* Contact Information */}
									{data.hide && (
										<div>
											<div className="row">
												<label className="mb-3 h4" style={{ color: "green" }}>
													Personal Information
												</label>

												{/* Surname input */}
												<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
													<label
														className="form-label"
														style={{ color: data.colorSurname }}
													>
														Surname <span className="text-danger">*</span>
													</label>
													<div className="input-group input-group-outline mb-3">
														<input
															className="form-control shadow-none"
															type="text"
															value={data.surname}
															name="surname"
															onChange={handleChange}
														/>
													</div>
												</div>

												{/* Previous Surname input */}
												<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
													<label className="form-label text-dark">
														Previous Surname (If applicable)
													</label>
													<div className="input-group input-group-outline mb-3">
														<input
															className="form-control shadow-none"
															type="text"
															value={data.prevSurname}
															name="prevSurname"
															onChange={handleChange}
														/>
													</div>
												</div>

												{/* Other Names input */}
												<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
													<label
														className="form-label"
														style={{ color: data.colorOthername }}
													>
														Other name(s) <span className="text-danger">*</span>
													</label>
													<div className="input-group input-group-outline mb-3">
														<input
															className="form-control shadow-none"
															type="text"
															value={data.otherNames}
															name="otherNames"
															onChange={handleChange}
														/>
													</div>
												</div>

												{/* Title input */}
												<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
													<label
														className="form-label"
														style={{ color: data.colorTitle }}
													>
														Title <span className="text-danger">*</span>
													</label>
													<div className="input-group input-group-outline mb-3">
														<input
															className="form-control shadow-none"
															type="text"
															value={data.title}
															name="title"
															onChange={handleChange}
														/>
													</div>
												</div>

												{/* DOB input */}
												<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
													<label
														className="form-label"
														style={{ color: data.colorDob }}
													>
														DOB <span className="text-danger">*</span>
													</label>
													<div className="input-group input-group-outline mb-3">
														<input
															className="form-control shadow-none"
															type="date"
															value={moment(data.dob).format("YYYY-MM-DD")}
															name="dob"
															onChange={handleChange}
														/>
													</div>
												</div>

												{/* Nationality input */}
												<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
													<label
														className="form-label"
														style={{ color: data.colorNationality }}
													>
														Nationality <span className="text-danger">*</span>
													</label>
													<div className="input-group input-group-outline mb-3">
														<input
															type="text"
															className="form-control shadow-none"
															value={data.nationality}
															name="nationality"
															onChange={handleChange}
														/>
													</div>
												</div>

												{/* Gender input */}
												<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
													<label
														className="form-label"
														style={{ color: data.colorGender }}
													>
														Select Gender <span className="text-danger">*</span>
													</label>
													<div className="input-group input-group-outline mb-3">
														<input
															className="form-control shadow-none"
															type="text"
															value={data.gender}
															name="gender"
															onChange={handleChange}
														/>
													</div>
												</div>

												{/* State input */}
												<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
													<label
														className="form-label"
														style={{ color: data.colorState }}
													>
														Select State <span className="text-danger">*</span>
													</label>
													<div className="input-group input-group-outline mb-3">
														<input
															className="form-control shadow-none"
															type="text"
															value={data.state}
															name="state"
															onChange={handleChange}
														/>
													</div>
												</div>

												{/* LGA input */}
												{data.state && (
													<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
														<label
															className="form-label"
															style={{ color: data.colorLga }}
														>
															LGA <span className="text-danger">*</span>
														</label>
														<div className="input-group input-group-outline mb-3">
															<input
																className="form-control shadow-none"
																type="text"
																value={data.lga}
																name="lga"
																onChange={handleChange}
															/>
														</div>
													</div>
												)}
											</div>

											<hr />
										</div>

										//
									)}

									{/* Contact Information */}

									<div className="row" style={{ padding: 15 }}>
										<label className="mb-3 h4" style={{ color: "green" }}>
											Contact Information
										</label>

										{/* Phone input */}
										<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
											<label
												className="form-label"
												style={{ color: data.colorPhone }}
											>
												Phone <span className="text-danger">*</span>
											</label>
											<div
												className="input-group input-group-outline mb-3"
												style={{ display: "flex", flexDirection: "column" }}
											>
												<input
													className="form-control shadow-none"
													type="text"
													value={data.contactPhone}
													name="contactPhone"
													onChange={handleChange}
													style={{ width: "100%" }}
												/>
												<FormError error={errors.contactPhone} />
											</div>
										</div>

										{/* Email input */}
										<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
											<label
												className="form-label"
												style={{ color: data.colorEmail }}
											>
												Email <span className="text-danger">*</span>
											</label>
											<div
												className="input-group input-group-outline mb-3"
												style={{ display: "flex", flexDirection: "column" }}
											>
												<input
													className="form-control shadow-none"
													type="email"
													value={data.contactEmail}
													name="contactEmail"
													onChange={handleChange}
													style={{ width: "100%" }}
												/>
												<FormError error={errors.contactEmail} />
											</div>
										</div>

										{/* address text box */}

										<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
											<label
												className="form-label"
												style={{ color: data.colorAddress }}
											>
												Address <span className="text-danger">*</span>
											</label>
											<div
												className="input-group input-group-outline mb-3"
												style={{ display: "flex", flexDirection: "column" }}
											>
												<textarea
													className="form-control shadow-none"
													value={data.contactAddress}
													name="contactAddress"
													onChange={handleChange}
													style={{ width: "100%" }}
												/>
												<FormError error={errors.contactAddress} />
											</div>
										</div>
									</div>
									<hr />

									{/* Contact Information */}
									{/* organization Information */}

									<div className="row" style={{ padding: 15 }}>
										<label className="mb-3 h4" style={{ color: "green" }}>
											Organization Information
										</label>

										{/* Organization Name input */}
										<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
											<label
												className="form-label"
												style={{ color: data.colorPhone }}
											>
												Organization Name <span className="text-danger">*</span>
											</label>
											<div
												className="input-group input-group-outline mb-3"
												style={{ display: "flex", flexDirection: "column" }}
											>
												<input
													className="form-control shadow-none"
													type="text"
													value={data.organizationName}
													name="organizationName"
													onChange={handleChange}
													style={{ width: "100%" }}
												/>
												<FormError error={errors.organizationName} />
											</div>
										</div>

										{/* Organization Position input */}

										<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
											<label
												className="form-label"
												style={{ color: data.colorPhone }}
											>
												Position <span className="text-danger">*</span>
											</label>
											<div
												className="input-group input-group-outline mb-3"
												style={{ display: "flex", flexDirection: "column" }}
											>
												<input
													className="form-control shadow-none"
													type="text"
													value={data.organizationPosition}
													name="organizationPosition"
													onChange={handleChange}
													style={{ width: "100%" }}
												/>
												<FormError error={errors.organizationPosition} />
											</div>
										</div>

										{/* address text box */}

										<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
											<label
												className="form-label"
												style={{ color: data.colorAddress }}
											>
												Address <span className="text-danger">*</span>
											</label>
											<div
												className="input-group input-group-outline mb-3"
												style={{ display: "flex", flexDirection: "column" }}
											>
												<textarea
													className="form-control shadow-none"
													value={data.organizationAddress}
													name="organizationAddress"
													onChange={handleChange}
													style={{ width: "100%" }}
												/>
												<FormError error={errors.organizationAddress} />
											</div>
										</div>

										{/* Phone input */}
										<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
											<label
												className="form-label"
												style={{ color: data.colorPhone }}
											>
												Organization Telephone{" "}
												<span className="text-danger">*</span>
											</label>
											<div
												className="input-group input-group-outline mb-3"
												style={{ display: "flex", flexDirection: "column" }}
											>
												<input
													className="form-control shadow-none"
													type="text"
													value={data.organizationTelephone}
													name="organizationTelephone"
													onChange={handleChange}
													style={{ width: "100%" }}
												/>
												<FormError error={errors.organizationTelephone} />
											</div>
										</div>

										{/* Email input */}
										<div className="col-sm-6 col-lg-4 col-md-6 mb-3">
											<label
												className="form-label"
												style={{ color: data.colorEmail }}
											>
												Organization Email{" "}
												<span className="text-danger">*</span>
											</label>
											<div
												className="input-group input-group-outline mb-3"
												style={{ display: "flex", flexDirection: "column" }}
											>
												<input
													className="form-control shadow-none"
													type="email"
													value={data.organizationEmail}
													name="organizationEmail"
													onChange={handleChange}
													style={{ width: "100%" }}
												/>
												<FormError error={errors.organizationEmail} />
											</div>
										</div>
									</div>

									{/* organization Information */}

									<button
										className="btn btn-success px-20  justify-center"
										onClick={handleSubmit}
										type="button"
										disabled={isLoading}
									>
										{isLoading ? "Submitting..." : "Update your Information"}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</main>
		</div>
	);
}

const FormError = ({ error }) => {
	if (!error) return null;
	return <p className="text-danger font-bold text-xs mt-1">{error}</p>;
};
